(() => {
  if (customElements.get('jupityr-science-quiz')) return;
  customElements.define('jupityr-science-quiz', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.questions = Array.from(this.querySelectorAll('[data-quiz-question]'));
      this.answers = this.answers || Array(this.questions.length).fill(null);
      this.index = this.index || 0;
      this.completed = this.completed || false;
      this.storageKey = `jupityr-quiz-best:${this.id}:${this.questions.length}`;
      try { this.best = Number(sessionStorage.getItem(this.storageKey)) || 0; } catch { this.best = this.best || 0; }
      this.querySelector('[data-quiz-start]').hidden = this.questions.length === 0;
      this.updateBest();
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-quiz-option]');
        if (!input || this.completed) return;
        const index = this.questions.indexOf(input.closest('[data-quiz-question]'));
        this.answers[index] = input.value;
        this.updateNavigation();
      }, options);
      this.addEventListener('click', event => {
        const action = event.target.closest('[data-quiz-action]')?.dataset.quizAction;
        if (action === 'start') this.start(false);
        if (action === 'retake') this.start(true);
        if (action === 'back' && this.index > 0) { this.index--; this.showQuestion(); }
        if (action === 'next' && this.answers[this.index] !== null) {
          if (this.index === this.questions.length - 1) this.finish();
          else { this.index++; this.showQuestion(); }
        }
        const link = event.target.closest('[data-quiz-science], [data-quiz-chapter]');
        if (link) { this.track('science_cta'); this.followAnchor(event, link); }
        if (event.target.closest('[data-quiz-product]')) this.track('product_cta');
      }, options);
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-quiz-retake]');
        if (link?.getAttribute('href') !== `#${this.id}`) return;
        event.preventDefault();
        this.start(true);
      }, options);
      // Attribute quiz-to-product engagement without intercepting native purchasing.
      document.addEventListener('click', event => {
        if (!this.completed || this.contains(event.target)) return;
        if (event.target.closest('[href="#jupityr-page-top"], [href="#jupityr-offers"], product-form button[type="submit"], sticky-atc button')) this.track('product_cta');
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const question = event.target.closest('[data-quiz-question]');
        if (!question) return;
        this.completed = false;
        this.index = this.questions.indexOf(question);
        this.showQuestion(false);
      }, options);
    }
    disconnectedCallback() { this.controller?.abort(); }
    track(action) {
      if (window.Shopify?.designMode) return;
      const detail = { action, section_id: this.dataset.sectionId, question_count: this.questions.length };
      // Matches the theme's bubbling CustomEvent convention; no answer/health data is sent.
      this.dispatchEvent(new CustomEvent('jupityr:quiz', { bubbles: true, detail }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `jupityr_quiz_${action}`, ...detail });
    }
    scrollBehavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    followAnchor(event, link) {
      const target = document.getElementById(link.getAttribute('href')?.slice(1));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
      target.focus({ preventScroll: true });
    }
    updateBest() {
      this.querySelectorAll('[data-quiz-best]').forEach(output => { output.textContent = `${this.best} / ${this.questions.length}`; });
      this.querySelector('[data-quiz-best-line]').hidden = this.best === 0;
    }
    start(retake) {
      this.completed = false;
      this.index = 0;
      this.answers = Array(this.questions.length).fill(null);
      this.querySelectorAll('[data-quiz-option]').forEach(input => { input.checked = false; });
      this.querySelectorAll('[data-quiz-review] details').forEach(item => { item.open = false; });
      this.track(retake ? 'retake' : 'start');
      this.showQuestion();
    }
    showQuestion(focus = true) {
      this.querySelector('[data-quiz-intro]').hidden = true;
      this.querySelector('[data-quiz-results]').hidden = true;
      this.querySelector('[data-quiz-play]').hidden = false;
      this.questions.forEach((question, index) => { question.hidden = index !== this.index; });
      this.querySelector('[data-quiz-progress]').textContent = `${this.index + 1} / ${this.questions.length}`;
      this.querySelector('progress').value = this.index + 1;
      this.updateNavigation();
      if (focus) {
        this.questions[this.index].querySelector('legend').focus({ preventScroll: true });
        this.scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
      }
    }
    updateNavigation() {
      this.querySelector('[data-quiz-back]').disabled = this.index === 0;
      const next = this.querySelector('[data-quiz-next]');
      next.disabled = this.answers[this.index] === null;
      next.textContent = this.index === this.questions.length - 1 ? next.dataset.finishLabel : next.dataset.nextLabel;
    }
    finish() {
      if (this.answers.some(answer => answer === null)) return;
      const score = this.questions.reduce((total, question, index) => total + Number(this.answers[index] === question.dataset.correct), 0);
      const improved = score > this.best;
      this.best = Math.max(this.best, score);
      try { sessionStorage.setItem(this.storageKey, String(this.best)); } catch { /* Quiz works when storage is unavailable. */ }
      this.completed = true;
      const results = this.querySelector('[data-quiz-results]');
      this.querySelector('[data-quiz-play]').hidden = true;
      results.hidden = false;
      results.classList.toggle('is-personal-best', improved);
      this.querySelector('[data-quiz-score]').textContent = `${score} / ${this.questions.length}`;
      this.querySelector('[data-quiz-new-best]').hidden = !improved;
      const tier = score <= 3 ? 'curious' : score <= 6 ? 'explorer' : 'insider';
      this.querySelectorAll('[data-quiz-tier]').forEach(item => { item.hidden = item.dataset.quizTier !== tier; });
      this.querySelectorAll('[data-quiz-review-item]').forEach((item, index) => {
        const selected = this.questions[index].querySelector(`input[value="${this.answers[index]}"]`);
        item.querySelector('[data-quiz-your-answer]').textContent = selected.closest('label').querySelector('[data-option-text]').textContent;
        item.querySelector('[data-quiz-review-status]').textContent = this.answers[index] === this.questions[index].dataset.correct ? this.dataset.correctLabel : this.dataset.discoveryLabel;
      });
      this.updateBest();
      this.track('complete');
      this.querySelector('[data-quiz-score]').focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
    }
  });
})();
